import { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';

const DEFAULT_BRAND_COLORS = {
  primary: '#0F6E56',
  borderDark: '#085041',
  accent: '#5DCAA5',
  botAvatarBg: '#E1F5EE',
  onPrimary: '#E1F5EE'
};

const THEME_FONT_FAMILY =
  '"Public Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const API_BASE_URL = String(process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
const CHAT_PATH = '/ai/chat';
const CHAT_WIDGET_POSITION_KEY = 'chat_widget_position_v1';

function buildBrandPalette(muiTheme) {
  const palette = muiTheme?.palette || {};
  const primary = palette.primary || {};
  const secondary = palette.secondary || {};
  const info = palette.info || {};
  const background = palette.background || {};
  const text = palette.text || {};
  const mode = palette.mode;

  const action = secondary.main ? secondary : info;
  const headerBg = mode === 'dark' ? background.paper || '#212B36' : primary.main || '#212B36';
  const headerText = mode === 'dark' ? text.primary || '#FFFFFF' : primary.contrastText || '#FFFFFF';
  const headerAvatarBg = mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.16)';

  return {
    actionBg: action.main || DEFAULT_BRAND_COLORS.primary,
    actionBorder: action.dark || DEFAULT_BRAND_COLORS.borderDark,
    actionText: action.contrastText || DEFAULT_BRAND_COLORS.onPrimary,
    accent: action.light || DEFAULT_BRAND_COLORS.accent,
    headerBg,
    headerText,
    headerAvatarBg,
    botAvatarBg: background.neutral || DEFAULT_BRAND_COLORS.botAvatarBg
  };
}

function buildSurfacePalette(muiTheme) {
  const palette = muiTheme?.palette || {};
  const background = palette.background || {};
  const text = palette.text || {};
  const error = palette.error || {};
  const isDark = palette.mode === 'dark';

  return {
    panelBg: background.paper || '#FFFFFF',
    messagesBg: background.neutral || background.default || '#F4F6F8',
    border: palette.divider || 'rgba(145, 158, 171, 0.24)',
    botText: text.primary || '#212B36',
    inputBg: background.paper || '#FFFFFF',
    inputText: text.primary || '#212B36',
    panelShadow: muiTheme?.shadows?.[12] || '0 10px 26px rgba(15, 23, 42, 0.18)',
    fabShadow: muiTheme?.shadows?.[12] || '0 10px 26px rgba(15, 23, 42, 0.18)',
    errorBg: isDark ? 'rgba(239, 68, 68, 0.08)' : '#fff5f5',
    errorBorder: isDark ? '1px solid rgba(239,68,68,0.35)' : '1px solid rgba(239,68,68,0.25)',
    errorText: isDark ? error.light || '#fecaca' : error.dark || '#991b1b'
  };
}

const iconStyles = {
  display: 'block'
};

function IconChat({ size = 26, color = DEFAULT_BRAND_COLORS.onPrimary }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={iconStyles} aria-hidden="true">
      <path
        d="M20 12c0 3.866-3.582 7-8 7a9.3 9.3 0 0 1-2.376-.304L4 20l1.362-3.274A6.66 6.66 0 0 1 4 12c0-3.866 3.582-7 8-7s8 3.134 8 7Z"
        stroke={color}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M8 12h.01M12 12h.01M16 12h.01"
        stroke={color}
        strokeWidth="2.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconBot({ size = 18, color = DEFAULT_BRAND_COLORS.primary }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={iconStyles} aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.7" />
      <path d="M9.4 16.4 11.1 9.6 12.9 16.4" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.1 13.6h2.0" stroke={color} strokeWidth="1.9" strokeLinecap="round" />
      <path d="M14.9 9.6v6.8" stroke={color} strokeWidth="1.9" strokeLinecap="round" />
      <path d="M16.8 7.4l.4 1.4 1.4.4-1.4.4-.4 1.4-.4-1.4-1.4-.4 1.4-.4.4-1.4Z" fill={color} opacity="0.9" />
    </svg>
  );
}

function IconSparkle({ size = 14, color = DEFAULT_BRAND_COLORS.primary }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={iconStyles} aria-hidden="true">
      <path
        d="M12 3.2l1.3 4.6 4.6 1.3-4.6 1.3-1.3 4.6-1.3-4.6-4.6-1.3 4.6-1.3L12 3.2Z"
        fill={color}
      />
      <path
        d="M18.6 12.1l.7 2.3 2.3.7-2.3.7-.7 2.3-.7-2.3-2.3-.7 2.3-.7.7-2.3Z"
        fill={color}
        opacity="0.88"
      />
    </svg>
  );
}

function IconMinimize({ size = 22, color = DEFAULT_BRAND_COLORS.onPrimary }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={iconStyles} aria-hidden="true">
      <path d="M6 12h12" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function IconSend({ size = 18, color = DEFAULT_BRAND_COLORS.onPrimary }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={iconStyles} aria-hidden="true">
      <path
        d="M12 5v14"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M6.8 10.2 12 5l5.2 5.2"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconPaperclip({ size = 18, color = DEFAULT_BRAND_COLORS.onPrimary }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={iconStyles} aria-hidden="true">
      <path
        d="M8.5 12.8 14 7.3a3.2 3.2 0 0 1 4.5 4.5l-7.2 7.2a5 5 0 0 1-7.1-7.1l7-7"
        stroke={color}
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconClose({ size = 16, color = DEFAULT_BRAND_COLORS.onPrimary }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={iconStyles} aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" stroke={color} strokeWidth="2.1" strokeLinecap="round" />
    </svg>
  );
}

function extractErrorMessage({ response, text }) {
  const fallback = text || `HTTP ${response.status}`;
  const trimmed = String(text || '').trim();
  if (!trimmed) return fallback;
  try {
    const data = JSON.parse(trimmed);
    if (data && typeof data.message === 'string' && data.message.trim()) {
      return data.message.trim();
    }
  } catch {}
  if (response.status === 401) {
    return 'Sessione scaduta. Effettua di nuovo l’accesso.';
  }
  return fallback;
}

function buildInitialWelcomeMessage(userDisplayName) {
  const name = String(userDisplayName || '').trim();
  const greeting = name ? `Ciao ${name}!` : 'Ciao!';
  return {
    id: `m_${Date.now()}_${Math.random().toString(16).slice(2)}`,
    role: 'assistant',
    content: `${greeting} Sono l'assistente AI di Debito Bancario. Come posso aiutarti?`
  };
}

function buildErrorMessage(content) {
  return {
    id: `m_${Date.now()}_${Math.random().toString(16).slice(2)}`,
    role: 'assistant',
    content: content || 'Mi dispiace, si è verificato un errore. Riprova tra poco.'
  };
}

export default function ChatWidget() {
  const muiTheme = useMuiTheme();
  const auth = useAuth();
  const user = auth?.user || null;
  const authLoading = Boolean(auth?.loading);
  const isAuthed = !authLoading && Boolean(user);
  const userDisplayName = useMemo(() => {
    if (!user) return '';
    return [user?.name, user?.surname].filter(Boolean).join(' ').trim() || String(user?.name || '').trim();
  }, [user]);
  const brand = useMemo(() => buildBrandPalette(muiTheme), [muiTheme]);
  const surface = useMemo(() => buildSurfacePalette(muiTheme), [muiTheme]);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [attachedPdf, setAttachedPdf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTyping, setShowTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [position, setPosition] = useState(() => {
    try {
      const raw = localStorage.getItem(CHAT_WIDGET_POSITION_KEY);
      if (!raw) return { right: 16, bottom: 88 };
      const parsed = JSON.parse(raw);
      const nextRight = Number(parsed?.right);
      const nextBottom = Number(parsed?.bottom);
      if (!Number.isFinite(nextRight) || !Number.isFinite(nextBottom)) return { right: 16, bottom: 88 };
      return { right: nextRight, bottom: nextBottom };
    } catch {
      return { right: 16, bottom: 88 };
    }
  });

  const listRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const dragMovedRef = useRef(false);

  const clampPosition = (nextPosition, elementWidth, elementHeight) => {
    const viewportWidth = window.innerWidth || 0;
    const viewportHeight = window.innerHeight || 0;
    const maxRight = Math.max(8, viewportWidth - elementWidth - 8);
    const maxBottom = Math.max(8, viewportHeight - elementHeight - 8);
    return {
      right: Math.min(Math.max(Number(nextPosition.right) || 0, 8), maxRight),
      bottom: Math.min(Math.max(Number(nextPosition.bottom) || 0, 8), maxBottom)
    };
  };

  const autoAvoidOverlap = () => {
    if (open) return;
    const widgetRect = rootRef.current?.getBoundingClientRect?.();
    if (!widgetRect) return;
    if (!(widgetRect.width > 0 && widgetRect.height > 0)) return;

    const selectors = [
      '.MuiTablePagination-actions',
      '.MuiPagination-root',
      '.MuiDataGrid-footerContainer',
      '.MuiDataGrid-footer'
    ];
    const candidates = selectors
      .flatMap((selector) => Array.from(document.querySelectorAll(selector)))
      .filter((el) => el && typeof el.getBoundingClientRect === 'function');

    const targets = candidates
      .map((el) => el.getBoundingClientRect())
      .filter((rect) => rect && rect.width > 0 && rect.height > 0)
      .filter((rect) => rect.bottom >= 0 && rect.right >= 0 && rect.top <= (window.innerHeight || 0) && rect.left <= (window.innerWidth || 0));

    if (!targets.length) return;

    const gap = 10;
    const overlaps = (a, b) =>
      a.left < b.right &&
      a.right > b.left &&
      a.top < b.bottom &&
      a.bottom > b.top;

    const overlapping = targets.find((rect) => overlaps(widgetRect, rect));
    if (!overlapping) return;

    let nextRight = position.right;
    let nextBottom = position.bottom;

    const desiredBottomEdge = overlapping.top - gap;
    const deltaBottom = widgetRect.bottom - desiredBottomEdge;
    if (deltaBottom > 0) {
      nextBottom = nextBottom + deltaBottom;
    }

    const desiredRightEdge = overlapping.left - gap;
    const deltaRight = widgetRect.right - desiredRightEdge;
    if (deltaRight > 0 && deltaBottom <= 0) {
      nextRight = nextRight + deltaRight;
    }

    const clamped = clampPosition({ right: nextRight, bottom: nextBottom }, widgetRect.width, widgetRect.height);
    if (clamped.right !== position.right || clamped.bottom !== position.bottom) {
      setPosition(clamped);
    }
  };

  useEffect(() => {
    if (isAuthed) return;
    setOpen(false);
    setMessages([]);
    setInputValue('');
    setLoading(false);
    setShowTyping(false);
    setConversationId(null);
    setPosition({ right: 16, bottom: 88 });
  }, [isAuthed]);

  useEffect(() => {
    try {
      localStorage.setItem(CHAT_WIDGET_POSITION_KEY, JSON.stringify(position));
    } catch {}
  }, [position]);

  useEffect(() => {
    if (!isAuthed) return;
    if (open) return;
    let rafId = null;
    const schedule = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        autoAvoidOverlap();
      });
    };

    schedule();
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    const observer = new MutationObserver(schedule);
    if (document.body) {
      observer.observe(document.body, { subtree: true, childList: true, attributes: true });
    }
    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      observer.disconnect();
    };
  }, [isAuthed, open, position.right, position.bottom]);

  useEffect(() => {
    if (!open) return;
    if (!isAuthed) return;
    if (!messages.length) {
      setMessages([buildInitialWelcomeMessage(userDisplayName)]);
    }
  }, [open, isAuthed, messages.length, userDisplayName]);

  useEffect(() => {
    if (!open) return;
    if (!isAuthed) return;
    if (!userDisplayName) return;
    setMessages((prev) => {
      if (prev.length !== 1) return prev;
      const first = prev[0];
      if (!first || first.role !== 'assistant') return prev;
      if (!String(first.content || '').includes("assistente AI di Debito Bancario")) return prev;
      if (String(first.content || '').includes(`Ciao ${userDisplayName}`)) return prev;
      return [buildInitialWelcomeMessage(userDisplayName)];
    });
  }, [open, isAuthed, userDisplayName]);

  useEffect(() => {
    if (!open) return;
    if (!isAuthed) return;
    const timeoutId = window.setTimeout(() => {
      inputRef.current?.focus?.();
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [open, isAuthed]);

  useEffect(() => {
    if (!open) return;
    if (!isAuthed) return;
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [open, isAuthed, messages, loading]);

  const sendMessage = async () => {
    const content = String(inputValue || '').trim();
    const file = attachedPdf;
    if ((!content && !file) || loading) return;

    setInputValue('');
    setAttachedPdf(null);

    const userMessage = {
      id: `m_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      role: 'user',
      content:
        (content ? content : 'Analizza il PDF allegato.') +
        (file?.name ? `\nPDF: ${String(file.name)}` : '')
    };

    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setLoading(true);
    setShowTyping(true);

    try {
      const token = localStorage.getItem('token');
      let response;
      if (file) {
        const formData = new FormData();
        if (conversationId != null) formData.append('conversationId', String(conversationId));
        formData.append('message', content ? content : 'Analizza il PDF allegato.');
        formData.append('context', JSON.stringify({}));
        formData.append('file', file);
        response = await fetch(`${API_BASE_URL}${CHAT_PATH}`, {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: formData
        });
      } else {
        response = await fetch(`${API_BASE_URL}${CHAT_PATH}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            conversationId,
            message: content,
            context: {}
          })
        });
      }

      if (!response.ok) {
        const text = await response.text();
        const message = extractErrorMessage({ response, text });
        throw new Error(message);
      }

      setShowTyping(false);

      const convHeader = response.headers.get('X-Conversation-Id');
      if (convHeader) {
        const numericId = Number(convHeader);
        if (!Number.isNaN(numericId)) {
          setConversationId(numericId);
        }
      }

      const assistantMessageId = `m_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      const attachmentType = response.headers.get('X-AI-Attachment-Type');
      const attachmentPages = Number(response.headers.get('X-AI-Attachment-Pages') || 0) || 0;
      const attachmentChars = Number(response.headers.get('X-AI-Attachment-Chars') || 0) || 0;
      const attachmentTruncated = response.headers.get('X-AI-Attachment-Truncated') === '1';
      const attachmentProbablyScanned = response.headers.get('X-AI-Attachment-Probably-Scanned') === '1';
      const attachmentNameRaw = response.headers.get('X-AI-Attachment-Name') || '';
      let attachmentName = '';
      if (attachmentNameRaw) {
        try {
          attachmentName = decodeURIComponent(attachmentNameRaw);
        } catch {
          attachmentName = attachmentNameRaw;
        }
      }

      if (attachmentType === 'pdf' && (attachmentPages > 0 || attachmentChars > 0)) {
        const title = attachmentName ? `PDF letto: ${attachmentName}.` : 'PDF letto.';
        const parts = [];
        if (attachmentPages > 0) parts.push(`${attachmentPages} pagine`);
        if (attachmentChars > 0) parts.push(`${attachmentChars} caratteri estratti`);
        const details = parts.join(' · ');
        const suffix = attachmentTruncated ? ' (testo troncato).' : '.';
        setMessages((prev) => [
          ...prev,
          { id: `m_${Date.now()}_${Math.random().toString(16).slice(2)}`, role: 'assistant', content: `${title}${details ? ` ${details}` : ''}${suffix}` }
        ]);
      }
      if (attachmentType === 'pdf' && attachmentProbablyScanned) {
        const warning =
          'Nota: il PDF sembra una scansione/immagine e potrebbe non contenere testo estraibile. ' +
          'In quel caso il bot non riesce a “leggerlo” senza OCR.';
        setMessages((prev) => [
          ...prev,
          { id: `m_${Date.now()}_${Math.random().toString(16).slice(2)}`, role: 'assistant', content: warning }
        ]);
      }

      setMessages((prev) => [...prev, { id: assistantMessageId, role: 'assistant', content: '' }]);

      let assistantText = '';
      if (response.body && response.body.getReader) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let done = false;
        while (!done) {
          const { value, done: readerDone } = await reader.read();
          done = readerDone;
          if (value) {
            const chunk = decoder.decode(value);
            assistantText += chunk;
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantMessageId ? { ...m, content: (m.content || '') + chunk } : m))
            );
          }
        }
      } else {
        assistantText = await response.text();
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantMessageId ? { ...m, content: assistantText } : m))
        );
      }

      if (!assistantText.trim()) {
        const fallback =
          'Nessuna risposta dal modello AI. Verifica la configurazione o la quota del provider nelle impostazioni AI.';
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantMessageId ? { ...m, content: fallback } : m))
        );
      }
    } catch (err) {
      const message = err?.message || 'Errore richiesta chat';
      setMessages((prev) => [...prev, buildErrorMessage(message)]);
    } finally {
      setLoading(false);
      setShowTyping(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    if (event.shiftKey) return;
    event.preventDefault();
    sendMessage();
  };

  if (!isAuthed) return null;

  const startDrag = (event, elementWidth, elementHeight) => {
    if (event.button !== 0) return;
    dragMovedRef.current = false;
    const startX = event.clientX;
    const startY = event.clientY;
    const startRight = position.right;
    const startBottom = position.bottom;

    const handlePointerMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      if (!dragMovedRef.current && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
        dragMovedRef.current = true;
      }
      const viewportWidth = window.innerWidth || 0;
      const viewportHeight = window.innerHeight || 0;
      const maxRight = Math.max(8, viewportWidth - elementWidth - 8);
      const maxBottom = Math.max(8, viewportHeight - elementHeight - 8);
      const nextRight = Math.min(Math.max(startRight - dx, 8), maxRight);
      const nextBottom = Math.min(Math.max(startBottom - dy, 8), maxBottom);
      setPosition({ right: nextRight, bottom: nextBottom });
    };

    const handlePointerUp = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
  };

  return (
    <>
      <style>
        {`
          @keyframes cwDots {
            0% { transform: translateY(0); opacity: .4; }
            50% { transform: translateY(-2px); opacity: .9; }
            100% { transform: translateY(0); opacity: .4; }
          }
          .cw-panel {
            position: absolute;
            right: 0;
            bottom: 0;
            width: 340px;
            height: 520px;
            border-radius: 16px;
            background: ${surface.panelBg};
            box-shadow: ${surface.panelShadow};
            border: 1px solid ${surface.border};
            overflow: hidden;
            display: flex;
            flex-direction: column;
            transform: translateY(10px);
            opacity: 0;
            pointer-events: none;
            transition: opacity 180ms ease, transform 180ms ease;
          }
          .cw-panel-open {
            opacity: 1;
            transform: translateY(0);
            pointer-events: auto;
          }
          @media (max-width: 480px) {
            .cw-panel {
              width: calc(100vw - 16px);
              height: calc(100vh - 16px);
              border-radius: 14px;
            }
          }
        `}
      </style>

      <div
        ref={rootRef}
        style={{
          position: 'fixed',
          right: position.right,
          bottom: position.bottom,
          zIndex: 9999
        }}
      >
        {!open ? (
          <button
            type="button"
            aria-label="Apri chat"
            onClick={() => {
              if (dragMovedRef.current) {
                dragMovedRef.current = false;
                return;
              }
              setOpen(true);
            }}
            onPointerDown={(event) => startDrag(event, 64, 64)}
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: brand.actionBg,
              border: `0.5px solid ${brand.actionBorder}`,
              boxShadow: surface.fabShadow,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <span
              style={{
                position: 'relative',
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <IconChat color={brand.actionText} />
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  right: -8,
                  bottom: -8,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: surface.panelBg,
                  border: `0.5px solid ${surface.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <IconSparkle size={14} color={brand.actionBg} />
              </span>
            </span>
          </button>
        ) : null}

        <div ref={panelRef} className={`cw-panel ${open ? 'cw-panel-open' : ''}`}>
          <div
            style={{
              background: brand.headerBg,
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontFamily: THEME_FONT_FAMILY
            }}
            onPointerDown={(event) => {
              if (!open) return;
              if (event.target?.closest?.('button')) return;
              const rect = panelRef.current?.getBoundingClientRect?.();
              startDrag(event, rect?.width || 340, rect?.height || 520);
            }}
          >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: brand.headerAvatarBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto'
              }}
            >
              <IconBot color={brand.headerText} size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  color: brand.headerText,
                  fontSize: 15,
                  fontWeight: 500,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                DB AI Bot
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: brand.accent,
                    display: 'inline-block'
                  }}
                />
                <span style={{ color: brand.headerText, opacity: 0.8, fontSize: 12 }}>online</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            aria-label="Chiudi chat"
            onClick={() => setOpen(false)}
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              border: 'none',
              background: 'rgba(255,255,255,0.14)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <IconMinimize color={brand.headerText} />
          </button>
        </div>

        <div
          ref={listRef}
          style={{
            flex: 1,
            background: surface.messagesBg,
            padding: '14px 12px',
            overflowY: 'auto',
            fontFamily: THEME_FONT_FAMILY
          }}
        >
          {messages.map((m) => {
            const isUser = m.role === 'user';
            return (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  justifyContent: isUser ? 'flex-end' : 'flex-start',
                  marginBottom: 10
                }}
              >
                {!isUser ? (
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      background: brand.botAvatarBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: '0 0 auto',
                      marginRight: 8,
                      border: `0.5px solid ${surface.border}`
                    }}
                  >
                    <IconBot size={14} color={brand.actionBg} />
                  </div>
                ) : null}

                <div
                  style={{
                    maxWidth: 220,
                    padding: '10px 11px',
                    fontSize: 13,
                    lineHeight: 1.35,
                    borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                    background: isUser ? brand.actionBg : surface.panelBg,
                    color: isUser ? brand.actionText : surface.botText,
                    border: isUser ? 'none' : `0.5px solid ${surface.border}`,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {m.content}
                </div>
              </div>
            );
          })}

          {showTyping ? (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: brand.botAvatarBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: '0 0 auto',
                  marginRight: 8,
                  border: `0.5px solid ${surface.border}`
                }}
              >
                <IconBot size={14} color={brand.actionBg} />
              </div>
              <div
                style={{
                  maxWidth: 220,
                  padding: '10px 11px',
                  fontSize: 13,
                  lineHeight: 1.35,
                  borderRadius: '14px 14px 14px 4px',
                  background: surface.panelBg,
                  color: surface.botText,
                  border: `0.5px solid ${surface.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: '#9aa3af',
                      display: 'inline-block',
                      animation: `cwDots 900ms ${i * 120}ms infinite`
                    }}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div
          style={{
            borderTop: `0.5px solid ${surface.border}`,
            background: surface.panelBg,
            padding: '10px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontFamily: THEME_FONT_FAMILY
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
              setAttachedPdf(file);
              if (fileRef.current) {
                fileRef.current.value = '';
              }
              window.setTimeout(() => {
                inputRef.current?.focus?.();
              }, 0);
            }}
          />
          <button
            type="button"
            aria-label="Allega PDF"
            onClick={() => fileRef.current?.click?.()}
            disabled={loading}
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: `0.5px solid ${surface.border}`,
              background: loading ? `${surface.messagesBg}` : surface.panelBg,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <IconPaperclip color={surface.inputText} />
          </button>
          {attachedPdf ? (
            <div
              style={{
                maxWidth: 160,
                height: 30,
                borderRadius: 14,
                padding: '0 10px',
                border: `0.5px solid ${surface.border}`,
                background: surface.messagesBg,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                overflow: 'hidden'
              }}
            >
              <span style={{ fontSize: 12, color: surface.botText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {String(attachedPdf.name || 'documento.pdf')}
              </span>
              <button
                type="button"
                aria-label="Rimuovi PDF"
                onClick={() => setAttachedPdf(null)}
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  border: `0.5px solid ${surface.border}`,
                  background: surface.panelBg,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0
                }}
              >
                <IconClose color={surface.inputText} size={14} />
              </button>
            </div>
          ) : null}
          <input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={attachedPdf ? 'Scrivi una domanda sul PDF…' : 'Scrivi un messaggio…'}
            style={{
              flex: 1,
              height: 38,
              borderRadius: 20,
              border: `0.5px solid ${surface.border}`,
              padding: '0 12px',
              fontSize: 13,
              outline: 'none',
              background: surface.inputBg,
              color: surface.inputText
            }}
          />
          <button
            type="button"
            aria-label="Invia messaggio"
            onClick={sendMessage}
            disabled={loading || (!String(inputValue || '').trim() && !attachedPdf)}
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: `0.5px solid ${brand.actionBorder}`,
              background: loading || (!String(inputValue || '').trim() && !attachedPdf) ? `${brand.actionBg}88` : brand.actionBg,
              cursor: loading || (!String(inputValue || '').trim() && !attachedPdf) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <IconSend color={brand.actionText} />
          </button>
        </div>

        </div>
      </div>
    </>
  );
}
